import { useState, useCallback } from 'react';
import { marked } from 'marked';
import { captureClientException } from '@instamenu/posthog/client';

export type UseCopyToClipboardOptions = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  resetDelay?: number;
};

export function useCopyToClipboard(options: UseCopyToClipboardOptions = {}) {
  const { onSuccess, onError, resetDelay = 1200 } = options;
  const [copied, setCopied] = useState(false);

  const copy = useCallback(
    async (input: string): Promise<boolean> => {
      try {
        if (!navigator.clipboard) throw new Error('Clipboard API is unavailable');

        if (typeof navigator.clipboard.write === 'function' && 'ClipboardItem' in globalThis) {
          const htmlContent = await Promise.resolve(marked.parse(input));
          await navigator.clipboard.write([
            new globalThis.ClipboardItem({
              'text/html': new Blob([htmlContent], { type: 'text/html' }),
              'text/plain': new Blob([input], { type: 'text/plain' }),
            }),
          ]);
        } else {
          await navigator.clipboard.writeText(input);
        }

        setCopied(true);
        onSuccess?.();
        setTimeout(() => setCopied(false), resetDelay);
        return true;
      } catch (error) {
        const copyError = error instanceof Error ? error : new Error('Copy to clipboard failed');

        captureClientException(copyError, {
          source: 'react-hooks.copy-to-clipboard',
        });
        onError?.(copyError);
        return false;
      }
    },
    [onSuccess, onError, resetDelay],
  );

  return { copy, copied };
}
